// frontend/src/components/CampaignManager/dialogs/VersandDialog.tsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  useMediaQuery,
  useTheme,
  Divider
} from '@mui/material';
import { Versand, Campaign, Lottery, Werbeweg } from '../types';

interface Props {
  open: boolean;
  versand: Versand | null;
  campaigns: Campaign[];
  lotteries: Lottery[];
  werbewege: Werbeweg[];
  onClose: () => void;
  onSave: (versand: Partial<Versand>, id?: number) => Promise<void>;
}

export default function VersandDialog({ 
  open, 
  versand, 
  campaigns, 
  lotteries, 
  werbewege, 
  onClose, 
  onSave 
}: Props) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [form, setForm] = useState({
    kampagnen_id: 0,
    lotterie_id: 0,
    werbeweg_id: 0,
    versandbrief: '',
    inhalt_versandbrief: '',
    gutschrift_betrag: 0,
    gutschrift_prozent: 0,
    buchungstext: '',
    porto: 0,
    klasse_beginn: 0,
    verwendung_boh: true,
    anzahl_boh: 0,
    prozent_boh: 100,
    verwendung_bok: true,
    anzahl_bok: 0,
    prozent_bok: 0,
    verwendung_bom: false,
    anzahl_bom: 0,
    prozent_bom: 0,
    verwendung_wht: false,
    anzahl_wht: 0,
    prozent_wht: 0
  });

  const [saving, setSaving] = useState(false);

  // BOH/BOK müssen zusammen 100% ergeben
  const handleBohProzentChange = (value: number) => {
    const bohValue = Math.min(100, Math.max(0, value));
    setForm({
      ...form,
      prozent_boh: bohValue,
      prozent_bok: 100 - bohValue,
      verwendung_boh: bohValue > 0,
      verwendung_bok: (100 - bohValue) > 0
    });
  };

  const handleBokProzentChange = (value: number) => {
    const bokValue = Math.min(100, Math.max(0, value));
    setForm({
      ...form,
      prozent_bok: bokValue,
      prozent_boh: 100 - bokValue,
      verwendung_bok: bokValue > 0,
      verwendung_boh: (100 - bokValue) > 0
    });
  };

  useEffect(() => {
    if (versand) {
      // Ensure BOH + BOK = 100%
      const bohProzent = versand.prozent_boh || 0;
      const bokProzent = versand.prozent_bok || (100 - bohProzent);
      setForm({
        kampagnen_id: versand.kampagnen_id,
        lotterie_id: versand.lotterie_id || 0,
        werbeweg_id: versand.werbeweg_id || 0,
        versandbrief: versand.versandbrief || '',
        inhalt_versandbrief: versand.inhalt_versandbrief || '',
        gutschrift_betrag: versand.gutschrift_betrag || 0,
        gutschrift_prozent: versand.gutschrift_prozent || 0,
        buchungstext: versand.buchungstext || '',
        porto: versand.porto || 0,
        klasse_beginn: versand.klasse_beginn || 0,
        verwendung_boh: bohProzent > 0,
        anzahl_boh: versand.anzahl_boh || 0,
        prozent_boh: bohProzent,
        verwendung_bok: bokProzent > 0,
        anzahl_bok: versand.anzahl_bok || 0,
        prozent_bok: bokProzent,
        verwendung_bom: false,
        anzahl_bom: 0,
        prozent_bom: 0,
        verwendung_wht: false,
        anzahl_wht: 0,
        prozent_wht: 0
      });
    } else {
      setForm({
        kampagnen_id: campaigns[0]?.id || 0,
        lotterie_id: lotteries[0]?.id || 0,
        werbeweg_id: werbewege[0]?.id || 0,
        versandbrief: '',
        inhalt_versandbrief: '',
        gutschrift_betrag: 0,
        gutschrift_prozent: 0,
        buchungstext: '',
        porto: 0,
        klasse_beginn: 0,
        verwendung_boh: true,
        anzahl_boh: 0,
        prozent_boh: 100,
        verwendung_bok: false,
        anzahl_bok: 0,
        prozent_bok: 0,
        verwendung_bom: false,
        anzahl_bom: 0,
        prozent_bom: 0,
        verwendung_wht: false,
        anzahl_wht: 0,
        prozent_wht: 0
      });
    }
  }, [versand, campaigns, lotteries, werbewege, open]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(form, versand?.id);
      onClose();
    } catch (error) {
      console.error('Error saving versand:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => !saving && onClose()}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle>
        {versand ? 'Versand bearbeiten' : 'Neuer Versand'}
      </DialogTitle>
      
      <DialogContent>
        <Stack spacing={3} sx={{ pt: 2 }}>
          {/* Hauptauswahl */}
          <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: 2 }}>
            <FormControl fullWidth required>
              <InputLabel>Kampagne</InputLabel>
              <Select
                value={form.kampagnen_id}
                onChange={(e) => setForm({ ...form, kampagnen_id: Number(e.target.value) })}
                label="Kampagne"
                disabled={saving}
              >
                {campaigns.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Lotterie</InputLabel>
              <Select
                value={form.lotterie_id}
                onChange={(e) => setForm({ ...form, lotterie_id: Number(e.target.value) })}
                label="Lotterie"
                disabled={saving}
              >
                {lotteries.map(l => <MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>)}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Werbeweg</InputLabel>
              <Select
                value={form.werbeweg_id}
                onChange={(e) => setForm({ ...form, werbeweg_id: Number(e.target.value) })}
                label="Werbeweg"
                disabled={saving}
              >
                {werbewege.map(w => <MenuItem key={w.id} value={w.id}>{w.bezeichnung}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>

          <Divider />

          {/* Versandbrief */}
          <Typography variant="h6">Versandbrief</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 2fr', gap: 2 }}>
            <TextField
              fullWidth
              label="Versandbrief (Nr.)"
              value={form.versandbrief}
              onChange={(e) => setForm({ ...form, versandbrief: e.target.value })}
              disabled={saving}
              placeholder="z.B. 575"
            />
            <TextField
              fullWidth
              label="Inhalt Versandbrief"
              value={form.inhalt_versandbrief}
              onChange={(e) => setForm({ ...form, inhalt_versandbrief: e.target.value })}
              disabled={saving}
              placeholder="z.B. Extra Joker Plus"
            />
          </Box>

          <Divider />

          {/* Finanzen & Details */}
          <Typography variant="h6">Finanzen & Details</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: 2 }}>
            <TextField
              fullWidth
              type="number"
              label="Porto (€)"
              value={form.porto}
              onChange={(e) => setForm({ ...form, porto: Number(e.target.value) })}
              disabled={saving}
              inputProps={{ step: 0.01, min: 0 }}
            />
            <TextField
              fullWidth
              type="number"
              label="Gutschrift Betrag (€)"
              value={form.gutschrift_betrag}
              onChange={(e) => setForm({ ...form, gutschrift_betrag: Number(e.target.value) })}
              disabled={saving}
              inputProps={{ step: 0.01, min: 0 }}
            />
            <TextField
              fullWidth
              type="number"
              label="Gutschrift Prozent (%)"
              value={form.gutschrift_prozent}
              onChange={(e) => setForm({ ...form, gutschrift_prozent: Number(e.target.value) })}
              disabled={saving}
              inputProps={{ step: 0.01, min: 0, max: 100 }}
            />
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 2 }}>
            <TextField
              fullWidth
              label="Buchungstext"
              value={form.buchungstext}
              onChange={(e) => setForm({ ...form, buchungstext: e.target.value })}
              disabled={saving}
            />
            <TextField
              fullWidth
              type="number"
              label="Klasse Beginn"
              value={form.klasse_beginn}
              onChange={(e) => setForm({ ...form, klasse_beginn: Number(e.target.value) })}
              disabled={saving}
            />
          </Box>

          <Divider />

          {/* BOH / BOK Verteilung */}
          <Typography variant="h6">Verteilung BOH / BOK</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Die Summe von BOH und BOK muss immer 100% ergeben. Ändern Sie einen Wert und der andere wird automatisch angepasst.
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 3 }}>
            <Box sx={{ p: 2, bgcolor: form.prozent_boh > 0 ? 'success.50' : 'grey.100', borderRadius: 2, border: form.prozent_boh > 0 ? '2px solid' : '1px solid', borderColor: form.prozent_boh > 0 ? 'success.main' : 'grey.300' }}>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2, color: form.prozent_boh > 0 ? 'success.dark' : 'text.secondary' }}>
                BOH (Bestelloption Heft)
              </Typography>
              <TextField
                fullWidth
                type="number"
                label="BOH Prozent (%)"
                value={form.prozent_boh}
                onChange={(e) => handleBohProzentChange(Number(e.target.value))}
                disabled={saving}
                inputProps={{ min: 0, max: 100, step: 1 }}
                helperText={`${form.prozent_boh}% der Sendungen gehen an BOH`}
              />
            </Box>

            <Box sx={{ p: 2, bgcolor: form.prozent_bok > 0 ? 'info.50' : 'grey.100', borderRadius: 2, border: form.prozent_bok > 0 ? '2px solid' : '1px solid', borderColor: form.prozent_bok > 0 ? 'info.main' : 'grey.300' }}>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2, color: form.prozent_bok > 0 ? 'info.dark' : 'text.secondary' }}>
                BOK (Bestelloption Karte)
              </Typography>
              <TextField
                fullWidth
                type="number"
                label="BOK Prozent (%)"
                value={form.prozent_bok}
                onChange={(e) => handleBokProzentChange(Number(e.target.value))}
                disabled={saving}
                inputProps={{ min: 0, max: 100, step: 1 }}
                helperText={`${form.prozent_bok}% der Sendungen gehen an BOK`}
              />
            </Box>
          </Box>

          {/* Summen-Anzeige */}
          <Box sx={{
            mt: 2,
            p: 2,
            bgcolor: (form.prozent_boh + form.prozent_bok) === 100 ? 'success.50' : 'error.50',
            borderRadius: 1,
            border: '1px solid',
            borderColor: (form.prozent_boh + form.prozent_bok) === 100 ? 'success.main' : 'error.main'
          }}>
            <Typography variant="body1" fontWeight="bold" sx={{
              color: (form.prozent_boh + form.prozent_bok) === 100 ? 'success.dark' : 'error.dark',
              textAlign: 'center'
            }}>
              Summe: {form.prozent_boh + form.prozent_bok}% {(form.prozent_boh + form.prozent_bok) === 100 ? '✓' : '(muss 100% sein!)'}
            </Typography>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Abbrechen
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          sx={{ backgroundColor: '#003366' }}
          disabled={saving || !form.kampagnen_id}
        >
          {saving ? 'Speichern...' : 'Speichern'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
